import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchCatalogEntity, createCatalogEntity } from '../features/inventory/catalogCrudSlice';
import {
  fetchSuppliers,
  createPurchaseOrdersBySupplier,
  verifyReceivedPurchaseOrder,
} from '../features/inventory/stockManagerInventorySlice';
import { receiveVerifiedPurchaseToInventory } from '../features/inventory/inventoryMovementSlice';

const OPENING_REMARKS = 'OPENING STOCK — existed in the warehouse before the application. Entered from dispatch.';

const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

export const nextCatalogCode = (rows, fields, prefix) => {
  let max = 0;
  rows.forEach(row => fields.forEach(field => {
    const code = String(row?.[field] || '').trim();
    if (!code.startsWith(prefix)) return;
    const number = parseInt(code.slice(prefix.length), 10);
    if (!Number.isNaN(number)) max = Math.max(max, number);
  }));
  return `${prefix}${String(max + 1).padStart(3, '0')}`;
};

const makeBatchId = () => {
  const now = new Date();
  return Number(`${now.toISOString().slice(0, 10).replace(/-/g, '')}${String(now.getTime()).slice(-5)}`);
};

const CATALOG = {
  brands: { codeFields: ['brand_code', 'code'], prefix: 'MKB', names: ['brand_name_english', 'en_name'] },
  categories: { codeFields: ['category_code', 'code'], prefix: 'MKC', names: ['category_name_english', 'en_name'] },
  products: { codeFields: ['product_code', 'code'], prefix: 'MKP', names: ['product_name_eng', 'en_name', 'tel_name'] },
};
const nameOf = (entity, row) => CATALOG[entity].names.map(field => row?.[field]).find(Boolean) || '';

// Stock that physically existed before the application, entered from Dispatch:
// (new product -> brand/category/product/product barcode) -> opening-stock
// purchase (received) -> verified -> received into inventory -> onCreated.
export default function OpeningStockDispatchForm({ pack = null, initialName = '', warehouseId, units = [], onCreated, onCancel }) {
  const dispatch = useDispatch();
  const isNewProduct = !pack;
  const [lists, setLists] = useState({ suppliers: [], brands: [], categories: [], products: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({ name: initialName, brand: '', category: '', packQty: '', unitId: '',
    supplierId: '', stockPacks: '1', dispatchPacks: '1', exp: '', cost: '', mrp: '' });
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const entities = isNewProduct ? ['brands', 'categories', 'products'] : [];
        const [suppliers, ...rows] = await Promise.all([
          dispatch(fetchSuppliers()).unwrap(),
          ...entities.map(entity => dispatch(fetchCatalogEntity({ entity })).unwrap()),
        ]);
        if (!active) return;
        const next = { suppliers: suppliers || [], brands: [], categories: [], products: [] };
        rows.forEach(result => { next[result.entity] = result.data || []; });
        setLists(next);
        const opening = next.suppliers.find(supplier => /opening/i.test(supplier.stakeholder_name || ''));
        if (opening) setForm(previous => ({ ...previous, supplierId: previous.supplierId || String(opening.id) }));
      } catch (err) {
        if (active) setLoadError(String(err?.message || err || 'Could not load suppliers'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [dispatch, isNewProduct]);

  const set = field => event => setForm(previous => ({ ...previous, [field]: event.target.value }));

  const ensureCatalog = async (entity, name) => {
    const existing = lists[entity].find(row => sameName(nameOf(entity, row), name));
    if (existing) return existing;
    const { codeFields, prefix } = CATALOG[entity];
    return dispatch(createCatalogEntity({ entity, payload: { code: nextCatalogCode(lists[entity], codeFields, prefix), en_name: name.trim() } })).unwrap();
  };

  const submit = async () => {
    setError('');
    const stockPacks = Number(form.stockPacks), dispatchPacks = Number(form.dispatchPacks);
    const cost = Number(form.cost), mrp = Number(form.mrp);
    const supplier = lists.suppliers.find(item => String(item.id) === String(form.supplierId));
    const problem = (isNewProduct && !form.name.trim() && 'Enter the product name.')
      || (isNewProduct && !form.brand.trim() && 'Enter the brand.')
      || (isNewProduct && !form.category.trim() && 'Enter the category.')
      || (isNewProduct && !(Number(form.packQty) > 0) && 'Enter the pack size.')
      || (isNewProduct && !form.unitId && 'Select the pack unit.')
      || (!supplier && 'Select the supplier for the opening-stock purchase.')
      || (!(stockPacks > 0) && 'Enter the packs in stock.')
      || (!(dispatchPacks > 0) && 'Enter the packs to dispatch.')
      || (dispatchPacks > stockPacks && 'Packs to dispatch cannot be more than packs in stock.')
      || (!/^\d{4}-\d{2}-\d{2}$/.test(form.exp) && 'Enter the expiry date.')
      || (!(cost > 0) && 'Enter the purchase cost per pack.')
      || (!(mrp > 0) && 'Enter the MRP per pack.')
      || (mrp < cost && 'MRP cannot be below the purchase cost.');
    if (problem) { setError(problem); return; }

    let step = '';
    let purchaseNo = '';
    const run = label => { step = label; setBusy(label); };
    try {
      let barcode = pack;
      let productCode = pack?.product_code;
      if (isNewProduct) {
        run('Saving brand');
        const brand = await ensureCatalog('brands', form.brand);
        run('Saving category');
        const category = await ensureCatalog('categories', form.category);
        run('Saving product');
        const product = await ensureCatalog('products', form.name);
        productCode = product.product_code || product.code;
        run('Creating product barcode');
        barcode = await dispatch(createCatalogEntity({ entity: 'product-barcodes', payload: {
          product_id: Number(product.id), brand_id: Number(brand.id), category_id: Number(category.id),
          unit_id: Number(form.unitId), quantity: Number(form.packQty) } })).unwrap();
      }

      run('Creating opening-stock purchase');
      const [created] = await dispatch(createPurchaseOrdersBySupplier({
        warehouse_id: Number(warehouseId), openingStock: true, remarks: OPENING_REMARKS,
        supplierGroups: [{ supplier_id: Number(supplier.id), supplier_name: supplier.stakeholder_name, items: [{
          product_barcode_id: Number(barcode.id), product_id: Number(barcode.product_id),
          brand_id: Number(barcode.brand_id), category_id: Number(barcode.category_id), unit_id: Number(barcode.unit_id),
          qty: Number(barcode.quantity), no_of_units: stockPacks, expected_unit_price: cost, actual_unit_price: cost,
        }] }],
      })).unwrap();
      const purchase = created?.purchaseOrder;
      const purchaseItem = created?.items?.[0];
      if (!purchase?.id || !purchaseItem?.id) throw new Error('the purchase was not returned');
      purchaseNo = purchase.po_number || `#${purchase.id}`;

      run(`Verifying purchase ${purchaseNo}`);
      await dispatch(verifyReceivedPurchaseOrder({ id: purchase.id, remarks: OPENING_REMARKS,
        items: [{ id: purchaseItem.id, actual_unit_price: cost, is_verified: true, missing: false }] })).unwrap();

      run(`Adding purchase ${purchaseNo} to inventory`);
      const batchId = makeBatchId();
      const received = await dispatch(receiveVerifiedPurchaseToInventory({
        purchase_order_id: purchase.id, purchase_order_item_id: purchaseItem.id,
        product_barcode_id: Number(barcode.id), product_id: Number(barcode.product_id),
        batch_id: batchId, sku_id: `${productCode || 'P'}-B${batchId}-PB${barcode.id}`,
        warehouse_id: Number(warehouseId), supplier_id: Number(supplier.id), stakeholders_id: Number(supplier.id),
        qty: Number(barcode.quantity), no_of_units: stockPacks, unit_price: cost, unit_mrp: mrp,
        exp_date: form.exp, remarks: OPENING_REMARKS,
      })).unwrap();
      const inventoryId = received?.inventoryProduct?.id;
      if (!inventoryId) throw new Error('the inventory batch was not returned');

      onCreated?.({ inventoryProductId: String(inventoryId), units: dispatchPacks,
        message: `Opening stock recorded: purchase ${purchaseNo}, inventory batch ${inventoryId} (${stockPacks} packs). ${dispatchPacks} added to this dispatch.` });
    } catch (err) {
      const reason = String(err?.message || err || 'failed');
      setError(purchaseNo
        ? `${step} failed: ${reason}. Purchase ${purchaseNo} was created — finish it from Purchase Orders instead of submitting again.`
        : `${step} failed: ${reason}.`);
    } finally {
      setBusy('');
    }
  };

  const input = (field, label, props = {}) => (
    <label className="flex flex-col text-xs font-semibold text-gray-700">
      {label}
      <input aria-label={label} value={form[field]} onChange={set(field)} className="mt-1 rounded border px-2 py-1 text-sm font-normal" {...props} />
    </label>
  );
  const datalist = (entity, id) => (
    <datalist id={id}>{lists[entity].map(row => <option key={row.id} value={nameOf(entity, row)} />)}</datalist>
  );

  return (
    <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3" role="group" aria-label="Opening stock entry">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-bold text-amber-900">
          {isNewProduct ? 'New product — opening stock' : `Opening stock — ${pack.product_name_eng || pack.product_name_tel || pack.product_code} · ${pack.quantity || ''} ${pack.unit_short_code || ''}`}
        </h4>
        <button type="button" onClick={onCancel} className="rounded border bg-white px-2 py-1 text-xs">Cancel</button>
      </div>
      {loading && <p className="text-xs text-gray-600">Loading suppliers{isNewProduct ? ', brands and categories' : ''}…</p>}
      {loadError && <p role="alert" className="text-xs text-red-700">{loadError}</p>}
      {isNewProduct && (
        <div className="mb-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {input('name', 'Product name', { list: 'opening-products' })}
          {input('brand', 'Brand', { list: 'opening-brands' })}
          {input('category', 'Category', { list: 'opening-categories' })}
          {input('packQty', 'Pack size', { type: 'number', min: '0', step: 'any', placeholder: '500' })}
          <label className="flex flex-col text-xs font-semibold text-gray-700">
            Pack unit
            <select aria-label="Pack unit" value={form.unitId} onChange={set('unitId')} className="mt-1 rounded border px-2 py-1 text-sm font-normal">
              <option value="">Select</option>
              {units.map(unit => <option key={unit.id} value={unit.id}>{unit.unit_short_code || unit.unit_name}</option>)}
            </select>
          </label>
          {datalist('products', 'opening-products')}{datalist('brands', 'opening-brands')}{datalist('categories', 'opening-categories')}
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <label className="flex flex-col text-xs font-semibold text-gray-700">
          Supplier
          <select aria-label="Supplier" value={form.supplierId} onChange={set('supplierId')} className="mt-1 rounded border px-2 py-1 text-sm font-normal">
            <option value="">Select</option>
            {lists.suppliers.map(supplier => <option key={supplier.id} value={supplier.id}>{supplier.stakeholder_name}</option>)}
          </select>
        </label>
        {input('stockPacks', 'Packs in stock', { type: 'number', min: '1', step: 'any' })}
        {input('dispatchPacks', 'Packs to dispatch', { type: 'number', min: '1', step: 'any' })}
        {input('exp', 'Expiry', { type: 'date' })}
        {input('cost', 'Cost / pack ₹', { type: 'number', min: '0', step: 'any' })}
        {input('mrp', 'MRP / pack ₹', { type: 'number', min: '0', step: 'any' })}
      </div>
      {error && <p role="alert" className="mt-2 text-xs text-red-700">{error}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button type="button" disabled={Boolean(busy) || loading} onClick={submit}
          className="rounded bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60">
          {busy ? `${busy}…` : 'Create & add to dispatch'}
        </button>
        <span className="text-xs text-gray-600">
          {isNewProduct ? 'Creates the catalog product and pack (if new), then ' : ''}records an opening-stock purchase, verifies it into this warehouse's inventory and adds the packs to this dispatch.
        </span>
      </div>
    </div>
  );
}
